<template>
  <section class="ag-card ag-lista">
    <div class="ag-card__head">
      <div>
        <h3 class="ag-card__titulo">{{ titulo }}</h3>
        <div class="ag-card__sub">
          {{ gastos.length }} {{ gastos.length === 1 ? 'gasto' : 'gastos' }} · {{ euros(total) }}
        </div>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <slot name="filtros" />
        <div class="ag-seg" role="group" aria-label="Ordenar gastos">
          <button type="button" :class="{ activo: orden === 'fecha' }" @click="orden = 'fecha'">
            <i class="bi bi-calendar3 me-1"></i>Fecha
          </button>
          <button type="button" :class="{ activo: orden === 'importe' }" @click="orden = 'importe'">
            <i class="bi bi-sort-down me-1"></i>Importe
          </button>
        </div>
      </div>
    </div>

    <div class="ag-lista__buscar">
      <i class="bi bi-search"></i>
      <input v-model.trim="busqueda" type="search" placeholder="Buscar concepto…" aria-label="Buscar concepto" />
    </div>

    <p v-if="visibles.length === 0" class="ag-vacio mb-0">No hay gastos para mostrar</p>

    <template v-if="orden === 'fecha'">
      <div v-for="grupo in grupos" :key="grupo.dia" class="ag-lista__grupo">
        <div class="ag-lista__dia">
          <span class="ag-cap">{{ etiquetaDia(grupo.dia) }}</span>
          <span class="ag-cifra">{{ euros(grupo.total) }}</span>
        </div>
        <div v-for="g in grupo.items" :key="g.id" class="ag-lista__fila">
          <span class="ag-dot" :style="{ background: colorDe(g) }"></span>
          <div class="ag-lista__txt">
            <div class="ag-lista__concepto">{{ g.concepto || 'Sin concepto' }}</div>
            <div class="ag-lista__meta">
              {{ categoriaDe(g) }}<template v-if="g.tipo_division"> · {{ g.tipo_division }}</template>
              <template v-if="esCompartido(g)"> · tu parte {{ euros(g.cantidad) }}</template>
            </div>
          </div>
          <div class="ag-lista__importe ag-cifra">{{ euros(importe(g)) }}</div>
        </div>
      </div>
    </template>
    <template v-else>
      <div v-for="g in visibles" :key="g.id" class="ag-lista__fila ag-lista__fila--plana">
        <span class="ag-dot" :style="{ background: colorDe(g) }"></span>
        <div class="ag-lista__txt">
          <div class="ag-lista__concepto">{{ g.concepto || 'Sin concepto' }}</div>
          <div class="ag-lista__meta">
            <span class="ag-cap">{{ etiquetaDia(diaISO(g), true) }}</span> · {{ categoriaDe(g) }}
          </div>
        </div>
        <div class="ag-lista__importe">
          <span class="ag-cifra">{{ euros(importe(g)) }}</span>
          <span class="ag-lista__peso" :style="{ width: `${pesoRelativo(g) * 0.9}px`, background: colorDe(g) }"></span>
        </div>
      </div>
    </template>

    <div v-if="filtrados.length > limite" class="text-center mt-3">
      <button type="button" class="ag-btn" @click="limite += paso">
        Ver {{ Math.min(paso, filtrados.length - limite) }} más
        <span class="text-muted fw-normal">({{ filtrados.length - limite }} restantes)</span>
      </button>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { importe, diaISO, categoriaDe, euros, parseISO, COLOR_OTRAS } from '../../utils/analisisGastos';

export default {
  name: 'AgListaGastos',
  props: {
    gastos: { type: Array, default: () => [] },
    colores: { type: Object, default: () => ({}) },
    titulo: { type: String, default: 'Gastos' },
    paso: { type: Number, default: 25 }
  },
  setup(props) {
    const orden = ref('fecha');
    const busqueda = ref('');
    const limite = ref(props.paso);

    watch(() => [props.gastos, orden.value, busqueda.value], () => { limite.value = props.paso; });

    const filtrados = computed(() => {
      const q = busqueda.value.toLowerCase();
      const base = q
        ? props.gastos.filter((g) => (g.concepto || '').toLowerCase().includes(q) || categoriaDe(g).toLowerCase().includes(q))
        : props.gastos;
      const copia = [...base];
      if (orden.value === 'importe') copia.sort((a, b) => importe(b) - importe(a));
      else copia.sort((a, b) => (diaISO(b) > diaISO(a) ? 1 : diaISO(b) < diaISO(a) ? -1 : (b.id || 0) - (a.id || 0)));
      return copia;
    });

    const visibles = computed(() => filtrados.value.slice(0, limite.value));
    const total = computed(() => props.gastos.reduce((s, g) => s + importe(g), 0));
    const maxImporte = computed(() => Math.max(...filtrados.value.map(importe), 1));

    const grupos = computed(() => {
      const out = [];
      for (const g of visibles.value) {
        const dia = diaISO(g);
        let grupo = out[out.length - 1];
        if (!grupo || grupo.dia !== dia) {
          grupo = { dia, items: [], total: 0 };
          out.push(grupo);
        }
        grupo.items.push(g);
        grupo.total += importe(g);
      }
      return out;
    });

    const hoy = new Date();
    const hoyISO = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    const ayer = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 1);
    const ayerISO = `${ayer.getFullYear()}-${String(ayer.getMonth() + 1).padStart(2, '0')}-${String(ayer.getDate()).padStart(2, '0')}`;

    const etiquetaDia = (iso, corto = false) => {
      if (!iso) return 'Sin fecha';
      if (iso === hoyISO) return 'Hoy';
      if (iso === ayerISO) return 'Ayer';
      return parseISO(iso).toLocaleDateString('es-ES', corto
        ? { day: 'numeric', month: 'short', year: 'numeric' }
        : { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    };

    const colorDe = (g) => props.colores[categoriaDe(g)] || COLOR_OTRAS;
    const esCompartido = (g) => g.cantidad != null && Math.abs(parseFloat(g.cantidad) - importe(g)) > 0.004;
    const pesoRelativo = (g) => Math.max(4, (importe(g) / maxImporte.value) * 100);

    return {
      orden, busqueda, limite, filtrados, visibles, grupos, total,
      etiquetaDia, colorDe, esCompartido, pesoRelativo,
      importe, diaISO, categoriaDe, euros
    };
  }
};
</script>

<style scoped>
.ag-lista__buscar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--linea);
  border-radius: 999px;
  padding: 0.3rem 0.85rem;
  margin-bottom: 0.75rem;
  background: var(--papel);
  color: var(--tinta-suave);
}
.ag-lista__buscar input { border: 0; background: transparent; outline: none; flex: 1; min-width: 0; color: var(--tinta); }
.ag-lista__grupo + .ag-lista__grupo { margin-top: 0.6rem; }
.ag-lista__dia {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--tinta-suave);
  padding: 0.35rem 0.25rem;
  border-bottom: 1px solid var(--linea);
  position: sticky;
  top: 0;
  background: var(--superficie);
}
.ag-lista__fila {
  display: grid;
  grid-template-columns: 0.7rem minmax(0, 1fr) auto;
  gap: 0.7rem;
  align-items: center;
  padding: 0.55rem 0.25rem;
  border-bottom: 1px dashed rgba(22, 53, 40, 0.08);
}
.ag-lista__fila:last-child { border-bottom: 0; }
.ag-lista__concepto { font-weight: 600; font-size: 0.92rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ag-lista__meta { font-size: 0.76rem; color: var(--tinta-suave); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ag-lista__importe { text-align: right; white-space: nowrap; display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.ag-lista__peso { height: 3px; border-radius: 2px; display: block; min-width: 4px; max-width: 90px; opacity: 0.8; }
</style>
